"use client";

import { useState } from "react";
import { sendToAuth } from "../../lib/send-auth";

interface EmailSignupInputProps {
  variant?: "hero" | "video" | "ready";
}

type Variant = NonNullable<EmailSignupInputProps["variant"]>;

const BG = "var(--surface-hover)";
const BTN_BG = "var(--foreground)";
const BTN_TXT = "var(--background)";

const cfg = {
  hero: {
    w: "min(560px, calc(100vw - 48px))",
    h: "clamp(48px, 12vw, 52px)",
    btnW: "clamp(96px, 20vw, 110px)",
    btnH: "clamp(38px, 10vw, 42px)",
    inputId: "hero-email-input",
    hover: true,
  },
  video: {
    w: "100%",
    h: "clamp(48px, 12vw, 52px)",
    btnW: "clamp(90px, 27vw, 108px)",
    btnH: "clamp(38px, 10vw, 42px)",
    inputId: "video-email-input",
    hover: true, // Enabled hover color change for the video variant
  },
  ready: {
    w: "min(480px, calc(100vw - 48px))",
    h: 44,
    btnW: 88,
    btnH: 34,
    inputId: "ready-email-input",
    hover: true,
  },
} as const satisfies Record<
  Variant,
  {
    w: string;
    h: number | string;
    btnW: number | string;
    btnH: number | string;
    inputId: string;
    hover?: boolean;
  }
>;

export function EmailSignupInput({ variant = "hero" }: EmailSignupInputProps) {
  const [e, setE] = useState("");
  const [err, setErr] = useState(false);
  const [f, setF] = useState(false);
  const isVideo = variant === "video";
  const isReady = variant === "ready";

  const isValid = (v: string) => {
    const t = v.trim();
    return t.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
  };

  const submit = () => {
    if (!isValid(e)) {
      setErr(true);
      return;
    }
    setErr(false);
    sendToAuth(e);
  };

  const c = cfg[variant];
  const hasHover = "hover" in c && !!c.hover;
  const boxShadow = err
    ? "0 0 0 2px var(--primary)"
    : isReady && f
      ? "0 0 0 3px rgba(255,82,0,0.25)"
      : "none";

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          background: BG,
          borderRadius: "var(--pill-radius)",
          width: c.w,
          height: c.h,
          boxShadow,
          transition: "box-shadow 0.2s ease",
        }}
      >
        <input
          id={c.inputId}
          type="email"
          aria-label="Email address"
          placeholder="name@example.com"
          value={e}
          onChange={(ev) => {
            setE(ev.target.value);
            if (err) setErr(false);
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") submit();
          }}
          onFocus={() => isReady && setF(true)}
          onBlur={() => isReady && setF(false)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            paddingLeft: isVideo ? "clamp(16px, 4.5vw, 22px)" : isReady ? 18 : 24,
            paddingRight: isVideo
              ? "clamp(98px, 30vw, 130px)"
              : isReady
                ? 104
                : "clamp(108px, 25vw, 140px)",
            fontFamily: "var(--font-inter)",
            fontSize: isVideo ? "clamp(16px, 4.8vw, 20px)" : isReady ? 14 : 20,
            color: "var(--foreground)",
          }}
        />
        <button
          onClick={submit}
          style={{
            position: "absolute",
            top: "50%",
            right: 6,
            transform: "translateY(-50%)",
            background: BTN_BG,
            color: BTN_TXT,
            border: "none",
            borderRadius: "var(--pill-radius)",
            width: c.btnW,
            height: c.btnH,
            fontFamily: "var(--font-inter)",
            fontWeight: "normal",
            fontSize: isVideo ? "clamp(16px, 4.8vw, 20px)" : isReady ? 14 : 20,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: hasHover ? "background 0.4s ease, color 0.4s ease" : "none",
          }}
          onMouseEnter={(ev) => {
            if (hasHover) {
              ev.currentTarget.style.background = "var(--ink)";
              ev.currentTarget.style.color = "var(--paper)";
            }
          }}
          onMouseLeave={(ev) => {
            if (hasHover) {
              ev.currentTarget.style.background = BTN_BG;
              ev.currentTarget.style.color = BTN_TXT;
            }
          }}
        >
          Join!
        </button>
      </div>

      {err && (
        <div
          style={{
            position: "absolute",
            top: -40,
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--primary)",
            color: "var(--paper)",
            padding: "8px 16px",
            borderRadius: 6,
            fontFamily: "var(--font-inter)",
            fontSize: 14,
            fontWeight: 500,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            animation: "fadeIn 0.2s ease",
            boxShadow: "0 4px 12px rgba(255, 82, 0, 0.3)",
          }}
        >
          Enter a valid email address
          <div
            style={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid var(--primary)",
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}
